import {captureException} from '@sentry/browser';
import {DataStatus, useTranslationContext} from 'platform/components';
import {Box, Icon, Image, Show, Video} from 'platform/foundation';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {ReactEventHandler, useEffect, useReducer, useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DEFAULT_VIDEO_VARIANT_NAME} from '../consts/videoVariants';
import {LightboxVideo} from '../types/LightboxVideo';
import {getVideoVariant} from '../utils/getVideoVariant';

interface VideoItemProps extends RequiredTestIdProps {
  video: LightboxVideo;
  /**
   * The preferred variant type to play the video.
   * Quality level of the video.
   */
  preferredVariantType?: string;
  isActive?: boolean;
}

export function VideoItem(props: VideoItemProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, dispatch] = useReducer(fetchVideoReducer, fetchVideoInitialState);
  const [currentTime, setCurrentTime] = useState(0);

  const t = useTranslationContext();

  const isVideoPaused = () => videoRef.current?.paused;
  const isVideoLoaded = isNotNil(state.url);

  const pauseVideo = () => {
    videoRef.current?.pause();
  };

  const playVideo = (videoType?: string) => {
    const video = videoRef.current;
    if (video) {
      video.play().catch((error: Error) => {
        if (videoType === 'original') {
          dispatch({type: 'PENDING_VARIANTS'});
        } else {
          dispatch({type: 'ERROR', payload: error.message});
          captureException(error, {extra: {url: state.url, variant: videoType}});
        }
      });
    }
  };

  /**
   * @function loadVideo
   * @see {@link ../../docs/videoLoad.svg|Video load rules chart}
   */
  const loadVideo = (preferredVariantType?: string) => {
    dispatch({type: 'LOADING'});

    const video = getVideoVariant(props.video, preferredVariantType ?? DEFAULT_VIDEO_VARIANT_NAME);

    if (isNotNil(video?.url)) {
      dispatch({type: 'SUCCESS', payload: video.url});

      return 'SUCCESS';
    }

    dispatch({type: 'PENDING_VIDEO'});
    return 'PENDING_VIDEO';
  };

  const loadAndPlayVideo = (preferredVariantType?: string) => {
    const loadState = loadVideo(preferredVariantType);

    if (loadState === 'SUCCESS') {
      /**
       * short wait before component flips state
       * which turned out to be a lot less logical than checking whether the video loaded or not
       */
      setTimeout(() => playVideo(preferredVariantType), FLIP_STATE_TIMEOUT);
    }
  };

  const handlePreferredVariantChange = (preferredVariantType?: string) => {
    const video = videoRef.current;

    if (isVideoPaused() || !video) {
      return loadVideo(preferredVariantType);
    }

    loadAndPlayVideo(props.preferredVariantType);
  };

  useEffect(() => {
    if (!isVideoLoaded) {
      return;
    }

    // useEffect runs before the ref is reset, currentTime of the previous video can be set
    setCurrentTime(videoRef.current?.currentTime ?? 0);

    handlePreferredVariantChange(props.preferredVariantType);
  }, [props.preferredVariantType]);

  const handleVideoClick: ReactEventHandler<HTMLDivElement> | undefined = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isVideoLoaded || state.isErrored) {
      loadAndPlayVideo(props.preferredVariantType);
    } else {
      if (isVideoPaused()) {
        playVideo();
      } else {
        pauseVideo();
      }
    }
  };

  const errorMessage = match(state.error)
    .with('PENDING_VIDEO', () => t('platform.lightbox.pendingVideo'))
    .with('PENDING_VARIANTS', () => t('platform.lightbox.pendingVariants'))
    .otherwise(() => undefined); // Default message will be applied

  return (
    <div
      css={css`
        cursor: pointer;
        width: 100%;
        height: 100%;
        flex-direction: column;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${({theme}) => theme.colors.palettes.neutral['900']['100']};
        overflow: hidden;
        position: relative;

        &:focus-visible {
          outline: ${({theme}) => theme.outline.default} ${({theme}) => theme.colors.general.white};
          outline-offset: ${({theme}) => theme.outline.offset};
        }
      `}
      tabIndex={props.isActive ? 0 : -1}
      onClick={handleVideoClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleVideoClick(event);
        }
      }}
    >
      <DataStatus isError={state.isErrored} errorMessage={errorMessage}>
        {isNotNil(state.url) && state.isSucceeded ? (
          <Video
            ref={videoRef}
            src={state.url}
            startAt={currentTime}
            shouldHideControls={!props.isActive}
            data-testid={suffixTestId('videoItem', props)}
          />
        ) : (
          <>
            <Show when={isNotNil(props.video.coverImageUrl)}>
              <Box width="100%" height="100%" position="absolute">
                <Image
                  src={props.video.coverImageUrl}
                  width="100%"
                  height="100%"
                  fit="contain"
                  data-testid={suffixTestId('videoItem-coverImage', props)}
                />
              </Box>
            </Show>
            <Show when={isNil(props.video.coverImageUrl)}>
              <Box
                width="100%"
                height="100%"
                position="absolute"
                backgroundColor="palettes.neutral.10.40"
              />
            </Show>
            <Box
              position="relative"
              backgroundColor="palettes.black.900.40"
              borderRadius="circular"
              padding={2}
            >
              <Icon
                data-testid={suffixTestId('videoItem-empty', props)}
                value="AV/play_arrow"
                size={15}
                color="text.white"
              />
            </Box>
          </>
        )}
      </DataStatus>
    </div>
  );
}

type FetchVideoAction =
  | {type: 'LOADING'}
  | {type: 'SUCCESS'; payload: string}
  | {type: 'PENDING_VIDEO'} // Even original is not available yet
  | {type: 'PENDING_VARIANTS'} // Original is ready, but no variants yet
  | {type: 'ERROR'; payload?: string};

type FetchVideoState = {
  isLoading: boolean;
  isErrored: boolean;
  isSucceeded: boolean;
  error: string | null;
  url: string | null;
};

const fetchVideoInitialState: FetchVideoState = {
  isLoading: false,
  isErrored: false,
  isSucceeded: false,
  error: null,
  url: null,
};

const fetchVideoReducer = (state: FetchVideoState, action: FetchVideoAction): FetchVideoState =>
  match(action)
    .with({type: 'LOADING'}, () => ({
      ...state,
      isLoading: true,
      isErrored: false,
      isSucceeded: false,
      error: null,
    }))
    .with({type: 'SUCCESS'}, ({payload}) => ({
      ...state,
      isLoading: false,
      isErrored: false,
      isSucceeded: true,
      error: null,
      url: payload,
    }))
    .with({type: 'PENDING_VIDEO'}, () => ({
      ...state,
      isLoading: false,
      isSucceeded: false,
      isErrored: true,
      error: 'PENDING_VIDEO',
    }))
    .with({type: 'PENDING_VARIANTS'}, () => ({
      ...state,
      isLoading: false,
      isSucceeded: false,
      isErrored: true,
      error: 'PENDING_VARIANTS',
    }))
    .with({type: 'ERROR'}, ({payload}) => ({
      ...state,
      isLoading: false,
      isSucceeded: false,
      isErrored: true,
      error: payload ?? 'OTHER',
    }))
    .otherwise(() => state);

const FLIP_STATE_TIMEOUT = 500;
